import {announce} from '@github-ui/aria-live'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {isFeatureEnabledWithOptOut} from '@github-ui/feature-flags'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {isStaff} from '@github-ui/stats'
import {InfoIcon} from '@primer/octicons-react'
import {Banner, Label, Link} from '@primer/react'
import {Stack} from '@primer/react/experimental'
import {useEffect, useState, useSyncExternalStore} from 'react'
import {
  getEnvironmentStripTourRequest,
  hasPendingOnboardingAnnouncement,
  ONE_COPILOT_ENVIRONMENT_STRIP_TOUR_NOTICE,
  subscribeEnvironmentStripTourRequest,
  useHasOnboardingSurfaceClaimedForPage,
  usePendingOnboardingAnnouncement,
} from '@github-ui/copilot-chat-helpers/copilot-onboarding'

import {useNoticeDismissal} from './onboarding/use-notice-dismissal'

import styles from './ShipFeedbackBanner.module.css'

const FEEDBACK_FORM_URL =
  'https://forms.cloud.microsoft/pages/responsepage.aspx?id=v4j5cvGGr0GRqy180BHbR23lpgGrtuVEo3m_MsFm8bNUQjNDTUlVRjJFSURPV0QwQ0lYOTdUQk1HUy4u&route=shorturl'
const FEEDBACK_ANNOUNCEMENT = 'This experience is staff-shipped. Give feedback.'
const PRIVATE_PREVIEW_ANNOUNCEMENT = 'Private preview. Give feedback.'
const noTourRequest = () => null

interface ShipFeedbackBannerProps {
  inline?: boolean
  flush?: boolean
  treatment?: 'staffship' | 'private-preview'
  alignment?: 'start' | 'center' | 'end'
  showEnvironmentStripTour?: boolean
}

/**
 * Staff-only banner soliciting ship feedback while the Copilot experience is staff
 * shipped. Gated behind `one_copilot_chat` without the UI opt-out and must be removed before GA.
 */
export function ShipFeedbackBanner(props: ShipFeedbackBannerProps) {
  return props.showEnvironmentStripTour &&
    copilotFeatureFlags.oneCopilotEnvironmentStripTour &&
    isFeatureEnabledWithOptOut('one_copilot_chat', 'one_copilot_chat_ui_opt_out') ? (
    <TourGatedShipFeedbackBanner {...props} />
  ) : (
    <FeedbackBannerContent {...props} />
  )
}

function TourGatedShipFeedbackBanner(props: ShipFeedbackBannerProps) {
  const {isBrowser} = useRuntimeEnvironment()
  const ignoreTourDismissal = copilotFeatureFlags.oneCopilotEnvironmentStripTourIgnoreDismiss
  const tourDismissal = useNoticeDismissal(ONE_COPILOT_ENVIRONMENT_STRIP_TOUR_NOTICE, {
    enabled: isBrowser && isStaff() && !ignoreTourDismissal,
  })
  const tourRequest = useSyncExternalStore(
    subscribeEnvironmentStripTourRequest,
    getEnvironmentStripTourRequest,
    noTourRequest,
  )
  const [settled, setSettled] = useState(false)
  useEffect(() => {
    if (ignoreTourDismissal || tourDismissal.data !== true || tourRequest) return
    const frame = requestAnimationFrame(() => setSettled(true))
    return () => cancelAnimationFrame(frame)
  }, [ignoreTourDismissal, tourDismissal.data, tourRequest])
  return (
    <FeedbackBannerContent
      {...props}
      tourReady={settled && !ignoreTourDismissal && tourDismissal.data === true && !tourRequest}
    />
  )
}

function FeedbackBannerContent({
  inline = false,
  flush = true,
  treatment = 'staffship',
  alignment = 'start',
  tourReady = true,
}: ShipFeedbackBannerProps & {tourReady?: boolean}) {
  // `isStaff()` reads a browser-only meta tag that is absent during Alloy SSR, so
  // gating on it directly would cause a hydration mismatch. `isBrowser` returns
  // false during SSR and the first hydration render, keeping them identical, then
  // re-renders true on the client.
  const {isBrowser} = useRuntimeEnvironment()
  const hasOnboardingClaim = useHasOnboardingSurfaceClaimedForPage()
  const pendingAnnouncement = usePendingOnboardingAnnouncement()
  const eligible =
    isBrowser &&
    isStaff() &&
    isFeatureEnabledWithOptOut('one_copilot_chat', 'one_copilot_chat_ui_opt_out') &&
    !hasOnboardingClaim &&
    !pendingAnnouncement &&
    tourReady

  // The banner is inserted after hydration and its title is hidden, so
  // screen-reader users would otherwise not learn it appeared. Announce it
  // politely once it becomes eligible (WCAG 4.1.3).
  useEffect(() => {
    if (eligible && !hasPendingOnboardingAnnouncement()) {
      announce(treatment === 'private-preview' ? PRIVATE_PREVIEW_ANNOUNCEMENT : FEEDBACK_ANNOUNCEMENT)
    }
  }, [eligible, treatment])

  if (!eligible) {
    return null
  }

  if (treatment === 'private-preview') {
    return (
      <div
        className={inline ? styles.ShipFeedbackInline : styles.PrivatePreview}
        data-alignment={alignment}
        data-testid="chat-ship-feedback-banner"
      >
        <Stack direction="horizontal" gap="condensed" align="baseline">
          <Label variant="secondary">Private preview</Label>
          <Link className={styles.PrivatePreviewLink} href={FEEDBACK_FORM_URL} inline>
            Give feedback
          </Link>
        </Stack>
      </div>
    )
  }

  const description = (
    <>
      This experience is staff-shipped.{' '}
      <Link href={FEEDBACK_FORM_URL} inline>
        Give feedback
      </Link>
    </>
  )

  if (inline) {
    return (
      <div className={styles.ShipFeedbackInline} data-testid="chat-ship-feedback-banner">
        <InfoIcon className={styles.ShipFeedbackInlineIcon} size={16} aria-hidden="true" />
        <span>{description}</span>
      </div>
    )
  }

  return (
    <Banner
      className={flush ? styles.ShipFeedbackBanner : undefined}
      data-testid="chat-ship-feedback-banner"
      flush={flush}
      hideTitle
      title="Staff shipped experience"
      variant="info"
      description={description}
    />
  )
}
